import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useReactToPrint } from 'react-to-print';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { SearchableSelect } from '../components/ui/searchable-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  FileText, 
  Plus, 
  RefreshCw, 
  Search,
  Eye,
  Check,
  Send,
  Printer,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  IndianRupee,
  Pencil
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DebitNoteRegisterPage = () => {
  const { getAuthHeader } = useAuth();
  
  // Data state
  const [debitNotes, setDebitNotes] = useState([]);
  const [pendingDCs, setPendingDCs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Filter state
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialogs
  const [viewDialog, setViewDialog] = useState(false);
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [selectedDN, setSelectedDN] = useState(null);
  const [selectedDC, setSelectedDC] = useState(null);
  
  // Create form
  const [taxRate, setTaxRate] = useState(18);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [creating, setCreating] = useState(false);
  
  // Edit form
  const [editItems, setEditItems] = useState([]);
  const [editTaxRate, setEditTaxRate] = useState(18);
  const [editInvoiceNumber, setEditInvoiceNumber] = useState('');
  const [editInvoiceDate, setEditInvoiceDate] = useState('');
  const [editRemarks, setEditRemarks] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Print ref
  const printRef = useRef();

  // Fetch debit notes
  const fetchDebitNotes = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/debit-notes?limit=200`;
      if (selectedStatus !== 'all') url += `&status=${selectedStatus}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      
      const res = await fetch(url, { headers: getAuthHeader() });
      if (res.ok) {
        const data = await res.json();
        setDebitNotes(data.items || []);
      }
    } catch (err) {
      console.error('Failed to fetch debit notes:', err);
      toast.error('Failed to load debit notes');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, selectedStatus, searchQuery]);

  // Fetch pending DCs
  const fetchPendingDCs = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/debit-notes/pending-for-dc`, {
        headers: getAuthHeader()
      });
      if (res.ok) {
        const data = await res.json();
        setPendingDCs(data.items || []);
      }
    } catch (err) {
      console.error('Failed to fetch pending DCs:', err);
    }
  }, [getAuthHeader]);

  // Fetch summary
  const fetchSummary = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/debit-notes/summary`, {
        headers: getAuthHeader()
      });
      if (res.ok) {
        const data = await res.json();
        setSummary(data);
      }
    } catch (err) {
      console.error('Failed to fetch summary:', err);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchDebitNotes();
    fetchPendingDCs();
    fetchSummary();
  }, [fetchDebitNotes, fetchPendingDCs, fetchSummary]);

  // Status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { label: 'Draft', variant: 'secondary', icon: Clock },
      pending_approval: { label: 'Pending Approval', variant: 'warning', icon: AlertCircle },
      approved: { label: 'Approved', variant: 'success', icon: CheckCircle2 },
      sent_to_supplier: { label: 'Sent to Supplier', variant: 'default', icon: Send },
      acknowledged: { label: 'Acknowledged', variant: 'success', icon: Check },
      cancelled: { label: 'Cancelled', variant: 'destructive', icon: XCircle }
    };
    const config = statusConfig[status] || { label: status, variant: 'secondary', icon: Clock };
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Create Debit Note
  const handleCreateDN = async () => {
    if (!selectedDC) return;
    
    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/debit-notes`, {
        method: 'POST',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          dc_id: selectedDC.id,
          tax_rate: taxRate,
          original_invoice_number: invoiceNumber || null,
          original_invoice_date: invoiceDate || null
        })
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        toast.success(data.message);
        setCreateDialog(false);
        setSelectedDC(null);
        setTaxRate(18);
        setInvoiceNumber('');
        setInvoiceDate('');
        fetchDebitNotes();
        fetchPendingDCs();
        fetchSummary();
      } else {
        toast.error(data.detail || 'Failed to create debit note');
      }
    } catch (err) {
      console.error('Failed to create DN:', err);
      toast.error('Failed to create debit note');
    } finally {
      setCreating(false);
    }
  };

  // Approve DN
  const handleApprove = async (dn) => {
    try {
      const res = await fetch(`${API_URL}/api/debit-notes/${dn.id}/approve`, {
        method: 'POST',
        headers: getAuthHeader()
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(data.message);
        fetchDebitNotes();
        fetchSummary();
      } else {
        toast.error(data.detail || 'Failed to approve');
      }
    } catch (err) {
      toast.error('Failed to approve debit note');
    }
  };

  // Submit DN
  const handleSubmit = async (dn) => {
    try {
      const res = await fetch(`${API_URL}/api/debit-notes/${dn.id}/submit`, {
        method: 'POST',
        headers: getAuthHeader()
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(data.message);
        fetchDebitNotes();
        fetchSummary();
      } else {
        toast.error(data.detail || 'Failed to submit');
      }
    } catch (err) {
      toast.error('Failed to submit debit note');
    }
  };

  // Open Edit Dialog
  const openEditDialog = (dn) => {
    setSelectedDN(dn);
    setEditItems(dn.items?.map(item => ({
      ...item,
      unit_price: item.unit_price || 0
    })) || []);
    setEditTaxRate(dn.tax_rate || 18);
    setEditInvoiceNumber(dn.original_invoice_number || '');
    setEditInvoiceDate(dn.original_invoice_date || '');
    setEditRemarks(dn.remarks || '');
    setEditDialog(true);
  };

  // Handle item price change
  const handleItemPriceChange = (index, price) => {
    const newItems = [...editItems];
    newItems[index].unit_price = parseFloat(price) || 0;
    newItems[index].amount = newItems[index].quantity * newItems[index].unit_price;
    setEditItems(newItems);
  };

  // Calculate totals for edit form
  const calculateEditTotals = () => {
    const subtotal = editItems.reduce((sum, item) => sum + (item.quantity * (item.unit_price || 0)), 0);
    const taxAmount = subtotal * (editTaxRate / 100);
    const total = subtotal + taxAmount;
    return { subtotal, taxAmount, total };
  };

  // Save edited DN
  const handleSaveEdit = async () => {
    if (!selectedDN) return;
    
    setSaving(true);
    try {
      const itemsToSave = editItems.map(item => ({
        item_id: item.item_id || null,
        item_name: item.item_name,
        quantity: item.quantity,
        unit: item.unit || 'NOS',
        unit_price: item.unit_price || 0,
        hsn_code: item.hsn_code || null,
        rejection_reason: item.rejection_reason || null
      }));
      
      const res = await fetch(`${API_URL}/api/debit-notes/${selectedDN.id}`, {
        method: 'PATCH',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: itemsToSave,
          tax_rate: editTaxRate,
          original_invoice_number: editInvoiceNumber || null,
          original_invoice_date: editInvoiceDate || null,
          remarks: editRemarks || null
        })
      });
      
      if (res.ok) {
        toast.success('Debit Note updated successfully');
        setEditDialog(false);
        fetchDebitNotes();
        fetchSummary();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to update');
      }
    } catch (err) {
      console.error('Failed to update DN:', err);
      toast.error('Failed to update debit note');
    } finally {
      setSaving(false);
    }
  };

  // Print handler
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: selectedDN ? `DebitNote_${selectedDN.debit_note_number}` : 'DebitNote'
  });

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="debit-note-register-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Debit Note Register</h1>
          <p className="text-sm text-gray-500">Track purchase returns and supplier debit notes</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => { fetchDebitNotes(); fetchPendingDCs(); fetchSummary(); }}
            disabled={loading}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {pendingDCs.length > 0 && (
            <Button
              onClick={() => setCreateDialog(true)}
              data-testid="create-dn-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Debit Note ({pendingDCs.length})
            </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold">{summary.total_count || 0}</div>
              <p className="text-xs text-gray-500">Total DNs</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-blue-600">
                {formatCurrency(summary.total_amount)}
              </div>
              <p className="text-xs text-gray-500">Total Value</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-gray-600">
                {summary.by_status?.draft?.count || 0}
              </div>
              <p className="text-xs text-gray-500">Draft</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-yellow-600">
                {summary.by_status?.pending_approval?.count || 0}
              </div>
              <p className="text-xs text-gray-500">Pending Approval</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-green-600">
                {summary.by_status?.approved?.count || 0}
              </div>
              <p className="text-xs text-gray-500">Approved</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-orange-600">
                {pendingDCs.length}
              </div>
              <p className="text-xs text-gray-500">Pending DCs</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm font-medium text-gray-700 mb-1 block">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="DN number, supplier, DC..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>
            
            <div className="w-[180px]">
              <label className="text-sm font-medium text-gray-700 mb-1 block">Status</label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger data-testid="status-filter">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="pending_approval">Pending Approval</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="sent_to_supplier">Sent to Supplier</SelectItem>
                  <SelectItem value="acknowledged">Acknowledged</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Debit Notes Table */}
      <Card>
        <CardContent className="pt-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-500">Loading...</span>
            </div>
          ) : debitNotes.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No debit notes found</p>
              {pendingDCs.length > 0 && (
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => setCreateDialog(true)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create from Pending DC
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>DN Number</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>DC Number</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {debitNotes.map((dn) => (
                  <TableRow key={dn.id} data-testid={`dn-row-${dn.debit_note_number}`}>
                    <TableCell className="font-medium">{dn.debit_note_number}</TableCell>
                    <TableCell>{dn.supplier_name}</TableCell>
                    <TableCell className="text-blue-600">{dn.dc_number}</TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(dn.total_amount)}
                    </TableCell>
                    <TableCell>{getStatusBadge(dn.status)}</TableCell>
                    <TableCell className="text-sm">{formatDate(dn.debit_note_date)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setSelectedDN(dn); setViewDialog(true); }}
                          data-testid={`view-dn-${dn.debit_note_number}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {dn.status === 'draft' && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(dn)}
                              title="Edit Prices"
                              data-testid={`edit-dn-${dn.debit_note_number}`}
                            >
                              <Pencil className="w-4 h-4 text-orange-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSubmit(dn)}
                              title="Submit for Approval"
                            >
                              <Send className="w-4 h-4 text-blue-600" />
                            </Button>
                          </>
                        )}
                        {dn.status === 'pending_approval' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleApprove(dn)}
                            title="Approve"
                          >
                            <Check className="w-4 h-4 text-green-600" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Debit Note</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Select Dispatched DC</label>
              <SearchableSelect
                options={pendingDCs.map(dc => ({ 
                  value: dc.id, 
                  label: `${dc.dc_number} - ${dc.party_name} (${dc.total_quantity} items)` 
                })).sort((a, b) => a.label.localeCompare(b.label))}
                value={selectedDC?.id || ''}
                onValueChange={(id) => setSelectedDC(pendingDCs.find(dc => dc.id === id))}
                placeholder="Select a DC..."
                searchPlaceholder="Search DC number or party..."
                emptyText="No dispatched DCs pending"
                data-testid="dc-select"
              />
            </div>
            
            {selectedDC && (
              <>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium mb-2">DC Details</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-gray-500">Supplier:</span> {selectedDC.party_name}</div>
                    <div><span className="text-gray-500">Items:</span> {selectedDC.items?.length || 0}</div>
                    <div><span className="text-gray-500">Total Qty:</span> {selectedDC.total_quantity}</div>
                    <div><span className="text-gray-500">GP:</span> {selectedDC.gate_pass_number || '-'}</div>
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium">GST Rate (%)</label>
                    <Input
                      type="number"
                      value={taxRate}
                      onChange={(e) => setTaxRate(parseFloat(e.target.value) || 0)}
                      min="0"
                      max="100"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Original Invoice #</label>
                    <Input
                      value={invoiceNumber}
                      onChange={(e) => setInvoiceNumber(e.target.value)}
                      placeholder="INV-001"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Invoice Date</label>
                    <Input
                      type="date"
                      value={invoiceDate}
                      onChange={(e) => setInvoiceDate(e.target.value)}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateDN} disabled={!selectedDC || creating}>
              {creating ? 'Creating...' : 'Create Debit Note'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialog} onOpenChange={setViewDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                {selectedDN?.debit_note_number}
              </span>
              <Button variant="outline" size="sm" onClick={handlePrint}>
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
            </DialogTitle>
          </DialogHeader>
          
          {selectedDN && (
            <div ref={printRef} className="space-y-4 p-4">
              {/* Print Header */}
              <div className="text-center border-b pb-4 print:block hidden">
                <h1 className="text-2xl font-bold">DEBIT NOTE</h1>
                <p className="text-sm text-gray-500">{selectedDN.debit_note_number}</p>
              </div>
              
              {/* DN Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500">Supplier</label>
                  <div className="font-medium">{selectedDN.supplier_name}</div>
                  {selectedDN.supplier_gstin && (
                    <div className="text-xs text-gray-500">GSTIN: {selectedDN.supplier_gstin}</div>
                  )}
                </div>
                <div>
                  <label className="text-xs text-gray-500">Status</label>
                  <div>{getStatusBadge(selectedDN.status)}</div>
                </div>
                <div>
                  <label className="text-xs text-gray-500">DC Number</label>
                  <div className="font-medium text-blue-600">{selectedDN.dc_number}</div>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Gate Pass</label>
                  <div>{selectedDN.gate_pass_number || '-'}</div>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Date</label>
                  <div>{formatDate(selectedDN.debit_note_date)}</div>
                </div>
                {selectedDN.original_invoice_number && (
                  <div>
                    <label className="text-xs text-gray-500">Original Invoice</label>
                    <div>{selectedDN.original_invoice_number}</div>
                  </div>
                )}
              </div>
              
              {/* Items */}
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium mb-2">Items</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Unit Price</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedDN.items?.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <div>{item.item_name}</div>
                          {item.rejection_reason && (
                            <div className="text-xs text-red-500">{item.rejection_reason}</div>
                          )}
                        </TableCell>
                        <TableCell className="text-right">{item.quantity} {item.unit}</TableCell>
                        <TableCell className="text-right">{formatCurrency(item.unit_price)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(item.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              {/* Totals */}
              <div className="border-t pt-4">
                <div className="flex justify-end">
                  <div className="w-64 space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Subtotal:</span>
                      <span>{formatCurrency(selectedDN.subtotal)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">GST ({selectedDN.tax_rate}%):</span>
                      <span>{formatCurrency(selectedDN.tax_amount)}</span>
                    </div>
                    <div className="flex justify-between font-bold text-lg border-t pt-2">
                      <span>Total:</span>
                      <span>{formatCurrency(selectedDN.total_amount)}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Remarks */}
              {selectedDN.remarks && (
                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium mb-1">Remarks</h4>
                  <p className="text-sm text-gray-600">{selectedDN.remarks}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Pricing Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5" />
              Edit Debit Note - {selectedDN?.debit_note_number}
            </DialogTitle>
          </DialogHeader>
          
          {selectedDN && (
            <div className="space-y-4">
              {/* Info Banner */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                <p className="font-medium text-blue-800">Enter unit prices for each item</p>
                <p className="text-blue-600">Supplier: {selectedDN.supplier_name} | DC: {selectedDN.dc_number}</p>
              </div>
              
              {/* Items with Price Input */}
              <div>
                <h4 className="text-sm font-medium mb-2">Items</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[300px]">Item</TableHead>
                      <TableHead className="text-right w-[100px]">Qty</TableHead>
                      <TableHead className="text-right w-[150px]">Unit Price (₹)</TableHead>
                      <TableHead className="text-right w-[150px]">Amount (₹)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {editItems.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <div className="font-medium">{item.item_name}</div>
                          {item.rejection_reason && (
                            <div className="text-xs text-red-500">{item.rejection_reason}</div>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {item.quantity} {item.unit}
                        </TableCell>
                        <TableCell className="text-right">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.unit_price || ''}
                            onChange={(e) => handleItemPriceChange(idx, e.target.value)}
                            className="w-[120px] text-right"
                            placeholder="0.00"
                            data-testid={`item-price-${idx}`}
                          />
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(item.quantity * (item.unit_price || 0))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              {/* Tax & Invoice Info */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium">GST Rate (%)</label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={editTaxRate}
                    onChange={(e) => setEditTaxRate(parseFloat(e.target.value) || 0)}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Original Invoice #</label>
                  <Input
                    value={editInvoiceNumber}
                    onChange={(e) => setEditInvoiceNumber(e.target.value)}
                    placeholder="INV-001"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Invoice Date</label>
                  <Input
                    type="date"
                    value={editInvoiceDate}
                    onChange={(e) => setEditInvoiceDate(e.target.value)}
                  />
                </div>
              </div>
              
              {/* Remarks */}
              <div>
                <label className="text-sm font-medium">Remarks</label>
                <Input
                  value={editRemarks}
                  onChange={(e) => setEditRemarks(e.target.value)}
                  placeholder="Additional notes..."
                />
              </div>
              
              {/* Totals */}
              <div className="border-t pt-4">
                <div className="flex justify-end">
                  <div className="w-64 space-y-2 bg-gray-50 p-4 rounded-lg">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Subtotal:</span>
                      <span className="font-medium">{formatCurrency(calculateEditTotals().subtotal)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">GST ({editTaxRate}%):</span>
                      <span>{formatCurrency(calculateEditTotals().taxAmount)}</span>
                    </div>
                    <div className="flex justify-between font-bold text-lg border-t pt-2">
                      <span>Total:</span>
                      <span className="text-blue-600">{formatCurrency(calculateEditTotals().total)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveEdit} 
              disabled={saving}
              data-testid="save-dn-btn"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </DashboardLayout>
  );
};

export default DebitNoteRegisterPage;
